<template>
  <div>
    <!-- <h1 class="mb-8 font-bold text-3xl">Reports</h1> -->
    <Head :title="__(title)" />

    <div class="mb-6 bg-white rounded shadow overflow-x-auto">
      <div class="p-6 space-y-6">
        <!-- Report Type Selection -->
        <div class="w-full md:w-1/4">
          <label class="block text-gray-700 mb-2">Report Type</label>
          <select v-model="form.reportType" class="form-select w-full">
            <option value="complaints">Feedback Summary</option>
            <option value="performance">Performance Metrics</option>
            <option value="root_cause">Root Cause Analysis</option>
            <option value="vulnerable">Vulnerable Customers</option>
          </select>
        </div>

        <!-- Time Frame Selection -->
        <div class="w-full md:w-1/4">
          <label class="block text-gray-700 mb-2">Time Frame</label>
          <select v-model="form.timeFrame" class="form-select w-full">
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="yearly">Yearly</option>
            <option value="custom">Custom Range</option>
          </select>
        </div>

        <!-- Custom Date Range (conditionally shown) -->
        <div v-if="form.timeFrame === 'custom'" class="w-full md:w-2/4 flex gap-4">
          <div class="w-1/2">
            <label class="block text-gray-700 mb-2">Start Date</label>
            <input v-model="form.startDate" type="date" class="form-input w-full" />
          </div>
          <div class="w-1/2">
            <label class="block text-gray-700 mb-2">End Date</label>
            <input v-model="form.endDate" type="date" class="form-input w-full" />
          </div>
        </div>

        <!-- Month/Year Selection for non-custom ranges -->
        <div v-else class="w-full md:w-1/4">
          <label class="block text-gray-700 mb-2">Select Period</label>
          <select v-model="form.period" class="form-select w-full">
            <option v-for="(period, index) in availablePeriods" :key="index" :value="period.value">
              {{ period.label }}
            </option>
          </select>
        </div>

        <!-- Format Selection and Generate Button -->
        <div class="w-full md:w-1/4">
          <label class="block text-gray-700 mb-2">Export Format</label>
          <select v-model="form.format" class="form-select w-full">
            <option value="html">HTML (View in Browser)</option>
            <option value="pdf">PDF</option>
            <option value="csv">CSV</option>
            <option value="excel">Excel</option>
          </select>
        </div>

        <button
          class="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2 dark:bg-blue-600 dark:hover:bg-blue-700 focus:outline-none dark:focus:ring-blue-800"
          @click="generateReport"
        >
          Generate Report
        </button>
      </div>
    </div>

<!-- Report Preview Area -->
  <div v-if="reportData" class="bg-white rounded shadow overflow-x-auto p-6">
    <div v-if="form.format === 'html'">
      <!-- HTML Report Content -->
      <h2 class="text-xl font-bold mb-4">{{ reportTitle }}</h2>
      <div v-html="reportData" />
    </div>
    <div v-else-if="form.format === 'pdf' && pdfPreviewUrl">
      <!-- PDF Preview with iframe -->
      <h2 class="text-xl font-bold mb-4">{{ reportTitle }}</h2>
      <iframe
        :src="pdfPreviewUrl"
        class="w-full h-screen border border-gray-200 rounded"
        frameborder="0"
      ></iframe>
      <div class="mt-4">
        <a :href="downloadUrl" class="btn-primary mt-4 inline-block">Download PDF</a>
      </div>
    </div>
    <div v-else>
      <p class="text-green-600">Your {{ form.format.toUpperCase() }} report is ready for download.</p>
      <a :href="downloadUrl" class="btn-primary mt-4 inline-block">Download Report</a>
    </div>
  </div>
  </div>
</template>


<script>
import Layout from '@/Shared/Layout'
import { Head } from '@inertiajs/vue3'
import axios from 'axios'

export default {
  metaInfo: { title: 'Reports' },
  components: {
    Head,
  },
  layout: Layout,
  props: {
    title: {
      type: String,
      default: 'Reports',
    },
  },

  data() {
    return {
      form: {
        reportType: 'complaints',
        timeFrame: 'monthly',
        period: this.getCurrentPeriod(),
        startDate: null,
        endDate: null,
        format: 'html',
      },
      reportData: null,
      downloadUrl: null,
      availablePeriods: [],
      pdfPreviewUrl: null,
    }
  },

  computed: {
    reportTitle() {
      const titles = {
        complaints: 'Feedback Summary Report',
        performance: 'Performance Metrics Report',
        root_cause: 'Root Cause Analysis Report',
        vulnerable: 'Vulnerable Customers Report',
      }
      return titles[this.form.reportType]
    },
  },

  watch: {
    'form.timeFrame': {
      immediate: true,
      handler() {
        this.updateAvailablePeriods()
      },
    },
  },

  methods: {
    getCurrentPeriod() {
      const now = new Date()
      return `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}`
    },

    updateAvailablePeriods() {
      // This would normally come from the backend, but we'll simulate it here
      const periods = []
      const now = new Date()

      if (this.form.timeFrame === 'monthly') {
        for (let i = 0; i < 12; i++) {
          const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
          periods.push({
            value: `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`,
            label: `${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`,
          })
        }
      } else if (this.form.timeFrame === 'quarterly') {
        for (let i = 0; i < 4; i++) {
          const quarter = Math.floor(now.getMonth() / 3) + 1 - i
          const year = quarter <= 0 ? now.getFullYear() - 1 : now.getFullYear()
          const adjustedQuarter = quarter <= 0 ? 4 + quarter : quarter
          periods.push({
            value: `${year}-Q${adjustedQuarter}`,
            label: `Q${adjustedQuarter} ${year}`,
          })
        }
      } else if (this.form.timeFrame === 'yearly') {
        for (let i = 0; i < 5; i++) {
          const year = now.getFullYear() - i
          periods.push({
            value: year.toString(),
            label: year.toString(),
          })
        }
      }

      this.availablePeriods = periods
      this.form.period = periods[0]?.value || ''
    },

       async generateReport() {
      try {
        this.isGenerating = true; // Add this data property for loading state

        const params = {
          report_type: this.form.reportType,
          time_frame: this.form.timeFrame,
          format: this.form.format,
        };

        if (this.form.timeFrame === 'custom') {
          params.start_date = this.form.startDate;
          params.end_date = this.form.endDate;
        } else {
          params.period = this.form.period;
        }

        // For HTML format
        if (this.form.format === 'html') {
          this.$inertia.post('/reports/generate', params, {
            preserveState: true,
            onSuccess: (page) => {
              this.reportData = page.props.reportData;
              this.pdfPreviewUrl = null;
              this.isGenerating = false;
            },
            onError: () => {
              this.isGenerating = false;
              alert('Failed to generate HTML report');
            }
          });
          return;
        }

        // For PDF format
        if (this.form.format === 'pdf') {
          try {
            const previewResponse = await axios.post('/reports/generate-preview', params);
            this.pdfPreviewUrl = previewResponse.data.preview_url;
            this.downloadUrl = previewResponse.data.download_url;
            this.reportData = 'PDF report generated';
          } catch (error) {
            console.error('PDF generation error:', error);
            alert('Failed to generate PDF preview');
          } finally {
            this.isGenerating = false;
          }
          return;
        }

        // For other formats (CSV, Excel)
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = '/reports/generate';
        form.target = '_blank';

        const csrfToken = document.querySelector('meta[name="csrf-token"]').content;
        const csrfInput = document.createElement('input');
        csrfInput.type = 'hidden';
        csrfInput.name = '_token';
        csrfInput.value = csrfToken;
        form.appendChild(csrfInput);

        for (const key in params) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = params[key];
          form.appendChild(input);
        }

        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
        this.isGenerating = false;

      } catch (error) {
        console.error('Error generating report:', error);
        this.isGenerating = false;
        alert('Failed to generate report. Please try again.');
      }
    },

  },
}
</script>
